import { getDisplayNameOrNameLabel } from '@src/utils/sorting';
import type { ITooltipParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import { get, isArray } from 'lodash-es';

import { AllocationValueTypeEnum, type IAllocation } from '../../../types';
import type { Leaves } from '../../../utils';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

type SubAccountColumnValue = string | string[] | null | undefined;

export interface SubAccountColumnParams<T extends object = any> {
  /** If you might be rendering Allocations, the tooltip needs to know where these are. Usually it's just "Allocations." */
  allocationField?: Leaves<T>;
  /** If you have Allocations set in quantity space, pass the currency here so we can render them correctly in the tooltip */
  allocationCurrencyField?: Leaves<T>;
}

/**
 * Converts a SubAccountColumnValue to a sortable string.
 * For arrays, sorts the display names for consistent comparison regardless of original order.
 */
function getSortableString(value: SubAccountColumnValue): string {
  if (value == null) {
    return '';
  }
  if (isArray(value)) {
    // Sort array of display names for consistent comparison regardless of original order
    return [...value].toSorted().join(', ');
  }
  return value;
}

/**
 * Comparator for sub account column values.
 * valueGetter already returns display names, so we just need to normalize arrays for consistent sorting.
 */
export function subAccountComparator(valueA: SubAccountColumnValue, valueB: SubAccountColumnValue): number {
  const strA = getSortableString(valueA);
  const strB = getSortableString(valueB);
  return strA.localeCompare(strB);
}

// Todo: this column should be redone and renamed to "allocations"
export const subAccount: ColDefFactory<Column<SubAccountColumnParams>> = column => ({
  ...baseColumn(column),
  tooltipValueGetter: ({ data, context, valueFormatted }: ITooltipParams<unknown, SubAccountColumnValue>) => {
    let currency: string | undefined = undefined;

    // In the tooltip, we always render helpfully if there are allocations on the object
    const allocation: IAllocation | undefined = column.params?.allocationField
      ? get(data, column.params.allocationField)
      : undefined;
    if (allocation) {
      currency = column.params?.allocationCurrencyField ? get(data, column.params?.allocationCurrencyField) : undefined;

      if (allocation.Allocations.length) {
        return allocation.Allocations.map(alloc => {
          const subAccount = context.current.subAccountsByName?.get(alloc.SubAccount);
          const name = getDisplayNameOrNameLabel(subAccount) || alloc.SubAccount;
          return allocation.ValueType === AllocationValueTypeEnum.Percentage
            ? `${Big(alloc.Value || 0).times(100)}% ${name}`
            : `${alloc.Value} ${currency || 'Qty'} ${name}`;
        }).join(',\n');
      }
    }

    return valueFormatted ?? '';
  },
  valueGetter: ({ data, context }: ValueGetterParams<any>): SubAccountColumnValue => {
    if (data == null) {
      // Address malformed records where the "parent row" of EntityAdmin does not exist...
      return '';
    }

    const resolveName = (value: string | number): string => {
      const subAccountByID = context.current.subAccountsByID;
      const subAccountByName = context.current.subAccountsByName;

      if (typeof value === 'number' && subAccountByID?.has(value)) {
        return getDisplayNameOrNameLabel(subAccountByID.get(value)) || String(value);
      }

      return getDisplayNameOrNameLabel(subAccountByName?.get(String(value))) || (value ? String(value) : '');
    };

    let subAccountNameOrId: string | number | string[] = '';

    if (column.field) {
      subAccountNameOrId = get(data, column.field);
    }
    if (data.SubAccount) {
      subAccountNameOrId = data.SubAccount;
    }

    if (isArray(subAccountNameOrId)) {
      return subAccountNameOrId.map((identifier: string) => resolveName(identifier) ?? identifier);
    }
    return resolveName(subAccountNameOrId);
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, SubAccountColumnValue>): string => {
    if (isArray(value)) {
      // Sort arrays for consistent display, matching comparator normalization
      return [...value].toSorted().join(', ');
    }

    return value ?? '';
  },
  comparator: subAccountComparator,
});
